/**
 * SKOP Creator Network Graph
 *
 * Interactive visualization of creator relationships and token history.
 */

import { useState, useEffect, useRef } from 'react';
import {
  buildCreatorNetwork,
  getNodeColor,
  getEdgeColor,
  getStatusColor,
  type CreatorNetwork,
  type NetworkNode,
  type CreatorNodeData,
  type TokenNodeData,
  type FunderNodeData,
} from '../lib/creator-network';

interface CreatorNetworkGraphProps {
  creatorAddress: string;
  width?: number;
  height?: number;
}

export function CreatorNetworkGraph({
  creatorAddress,
  width = 600,
  height = 400,
}: CreatorNetworkGraphProps) {
  const [network, setNetwork] = useState<CreatorNetwork | null>(null);
  const [selectedNode, setSelectedNode] = useState<NetworkNode | null>(null);
  const [hoveredNode, setHoveredNode] = useState<NetworkNode | null>(null);
  const [loading, setLoading] = useState(true);
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    setLoading(true);
    // Simulate async load
    setTimeout(() => {
      const net = buildCreatorNetwork(creatorAddress, 2);
      setNetwork(net);
      setLoading(false);
    }, 500);
  }, [creatorAddress]);

  if (loading) {
    return (
      <div style={{
        width,
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg-tertiary)',
        borderRadius: 'var(--radius-md)',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '24px', height: '24px', margin: '0 auto 8px', color: 'var(--accent)' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </div>
          <div style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>
            Building network graph...
          </div>
        </div>
      </div>
    );
  }

  if (!network) {
    return (
      <div style={{
        width,
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg-tertiary)',
        borderRadius: 'var(--radius-md)',
      }}>
        <div style={{ textAlign: 'center', color: 'var(--text-tertiary)' }}>
          No network data available
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '12px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ width: '16px', height: '16px', display: 'inline-flex', color: 'var(--accent)' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <circle cx="12" cy="12" r="4"/>
              <line x1="12" y1="2" x2="12" y2="6"/>
              <line x1="12" y1="18" x2="12" y2="22"/>
              <line x1="2" y1="12" x2="6" y2="12"/>
              <line x1="18" y1="12" x2="22" y2="12"/>
            </svg>
          </span>
          <span style={{ fontSize: '14px', fontWeight: 600 }}>Creator Network</span>
        </div>
        <NetworkStats stats={network.stats} />
      </div>

      {/* Graph Container */}
      <div style={{
        position: 'relative',
        background: 'var(--bg-tertiary)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
      }}>
        <svg
          ref={svgRef}
          width={width}
          height={height}
          style={{ display: 'block' }}
        >
          {/* Edges */}
          <g>
            {network.edges.map((edge) => {
              const source = network.nodes.find((n) => n.id === edge.source);
              const target = network.nodes.find((n) => n.id === edge.target);
              if (!source || !target) return null;

              const isHighlighted =
                selectedNode?.id === edge.source ||
                selectedNode?.id === edge.target ||
                hoveredNode?.id === edge.source ||
                hoveredNode?.id === edge.target;

              return (
                <line
                  key={edge.id}
                  x1={source.x}
                  y1={source.y}
                  x2={target.x}
                  y2={target.y}
                  stroke={getEdgeColor(edge.type)}
                  strokeWidth={isHighlighted ? 3 : 1.5}
                  strokeOpacity={isHighlighted ? 1 : 0.6}
                  style={{ transition: 'all 0.2s' }}
                />
              );
            })}
          </g>

          {/* Nodes */}
          <g>
            {network.nodes.map((node) => {
              const isSelected = selectedNode?.id === node.id;
              const isHovered = hoveredNode?.id === node.id;
              const isHighlighted = isSelected || isHovered;

              const radius = getNodeRadius(node);
              const color = node.type === 'token'
                ? getStatusColor((node.data as TokenNodeData).status)
                : getNodeColor(node.type);

              return (
                <g
                  key={node.id}
                  transform={`translate(${node.x}, ${node.y})`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedNode(isSelected ? null : node)}
                  onMouseEnter={() => setHoveredNode(node)}
                  onMouseLeave={() => setHoveredNode(null)}
                >
                  {/* Glow effect for highlighted */}
                  {isHighlighted && (
                    <circle
                      r={radius + 6}
                      fill={color}
                      opacity={0.3}
                    />
                  )}

                  {/* Main circle */}
                  <circle
                    r={radius}
                    fill={color}
                    stroke={isHighlighted ? '#fff' : 'transparent'}
                    strokeWidth={2}
                    style={{ transition: 'all 0.2s' }}
                  />

                  {/* Icon */}
                  <text
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={radius * 0.8}
                    fill="#000"
                  >
                    {getNodeIcon(node)}
                  </text>

                  {/* Label */}
                  <text
                    y={radius + 14}
                    textAnchor="middle"
                    fontSize="10"
                    fill="var(--text-secondary)"
                  >
                    {node.label}
                  </text>
                </g>
              );
            })}
          </g>
        </svg>

        {/* Legend */}
        <div style={{
          position: 'absolute',
          bottom: '12px',
          left: '12px',
          display: 'flex',
          gap: '12px',
          fontSize: '10px',
          color: 'var(--text-tertiary)',
        }}>
          <LegendItem color="#00ff9d" label="Creator" />
          <LegendItem color="#888888" label="Token" />
          <LegendItem color="#ffd93d" label="Funder" />
          <LegendItem color="#ff2d55" label="Rugged" />
        </div>
      </div>

      {/* Selected Node Details */}
      {selectedNode && (
        <NodeDetails
          node={selectedNode}
          onClose={() => setSelectedNode(null)}
        />
      )}
    </div>
  );
}

function NetworkStats({ stats }: { stats: CreatorNetwork['stats'] }) {
  return (
    <div style={{
      display: 'flex',
      gap: '16px',
      fontSize: '11px',
    }}>
      <StatBadge
        label="Creators"
        value={stats.totalCreators}
        color="var(--accent)"
      />
      <StatBadge
        label="Tokens"
        value={stats.totalTokens}
        color="var(--success)"
      />
      <StatBadge
        label="Rugs"
        value={stats.totalRugs}
        color="var(--error)"
      />
      <StatBadge
        label="Rug Rate"
        value={`${(stats.avgRugRate * 100).toFixed(0)}%`}
        color={stats.avgRugRate > 0.5 ? 'var(--error)' : 'var(--warning)'}
      />
    </div>
  );
}

function StatBadge({
  label,
  value,
  color,
}: {
  label: string;
  value: string | number;
  color: string;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <span style={{ color: 'var(--text-tertiary)' }}>{label}:</span>
      <span style={{ fontWeight: 600, color }}>{value}</span>
    </div>
  );
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <div style={{
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        background: color,
      }} />
      <span>{label}</span>
    </div>
  );
}

function NodeDetails({
  node,
  onClose,
}: {
  node: NetworkNode;
  onClose: () => void;
}) {
  return (
    <div style={{
      marginTop: '12px',
      padding: '16px',
      background: 'var(--bg-secondary)',
      borderRadius: 'var(--radius-md)',
      border: '1px solid var(--border)',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '12px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{
            width: '18px',
            height: '18px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            fontWeight: 700,
            background: `${getNodeColor(node.type)}30`,
            color: getNodeColor(node.type),
            borderRadius: '50%',
          }}>{getNodeIcon(node)}</span>
          <span style={{ fontWeight: 600 }}>{node.label}</span>
          <span style={{
            padding: '2px 6px',
            fontSize: '10px',
            background: `${getNodeColor(node.type)}20`,
            color: getNodeColor(node.type),
            borderRadius: 'var(--radius-sm)',
            textTransform: 'uppercase',
          }}>
            {node.type}
          </span>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-tertiary)',
            cursor: 'pointer',
            fontSize: '18px',
          }}
        >
          ×
        </button>
      </div>

      {/* Node-specific details */}
      {node.type === 'creator' && (
        <CreatorDetails data={node.data as CreatorNodeData} />
      )}
      {node.type === 'token' && (
        <TokenDetails data={node.data as TokenNodeData} />
      )}
      {node.type === 'funder' && (
        <FunderDetails data={node.data as FunderNodeData} />
      )}
    </div>
  );
}

function CreatorDetails({ data }: { data: CreatorNodeData }) {
  const rugRate = data.totalTokens > 0
    ? ((data.ruggedTokens / data.totalTokens) * 100).toFixed(0)
    : '0';

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '12px',
    }}>
      <DetailCard label="Total Tokens" value={data.totalTokens.toString()} />
      <DetailCard
        label="Rugged"
        value={data.ruggedTokens.toString()}
        color={data.ruggedTokens > 0 ? 'var(--error)' : undefined}
      />
      <DetailCard
        label="Rug Rate"
        value={`${rugRate}%`}
        color={Number(rugRate) > 50 ? 'var(--error)' : Number(rugRate) > 25 ? 'var(--warning)' : 'var(--success)'}
      />
      <DetailCard
        label="Reputation"
        value={data.reputation.toString()}
        color={data.reputation > 70 ? 'var(--success)' : data.reputation > 40 ? 'var(--warning)' : 'var(--error)'}
      />
      <DetailCard
        label="Volume"
        value={`$${formatNumber(data.totalVolumeUsd)}`}
      />
      <DetailCard
        label="Active Since"
        value={formatDate(data.firstSeen)}
      />
    </div>
  );
}

function TokenDetails({ data }: { data: TokenNodeData }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '12px',
    }}>
      <DetailCard label="Symbol" value={data.symbol} />
      <DetailCard
        label="Status"
        value={data.status.toUpperCase()}
        color={getStatusColor(data.status)}
      />
      <DetailCard
        label="Peak MC"
        value={`$${formatNumber(data.peakMarketCap)}`}
      />
      {data.status === 'rugged' && (
        <>
          <DetailCard
            label="Creator Profit"
            value={`${data.creatorProfit.toFixed(2)} SOL`}
            color="var(--error)"
          />
          <DetailCard
            label="Holders Rugged"
            value={data.holdersRugged.toString()}
            color="var(--error)"
          />
        </>
      )}
      <DetailCard
        label="Created"
        value={formatDate(data.createdAt)}
      />
    </div>
  );
}

function FunderDetails({ data }: { data: FunderNodeData }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '12px',
    }}>
      <DetailCard
        label="Funded Creators"
        value={data.fundedCreators.toString()}
      />
      <DetailCard
        label="Total Funding"
        value={`${data.totalFundingAmount.toFixed(2)} SOL`}
      />
      <DetailCard
        label="Suspicion Score"
        value={data.suspicionScore.toString()}
        color={data.suspicionScore > 70 ? 'var(--error)' : data.suspicionScore > 40 ? 'var(--warning)' : 'var(--success)'}
      />
    </div>
  );
}

function DetailCard({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) {
  return (
    <div style={{
      padding: '8px 12px',
      background: 'var(--bg-tertiary)',
      borderRadius: 'var(--radius-sm)',
    }}>
      <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginBottom: '2px' }}>
        {label}
      </div>
      <div style={{ fontSize: '14px', fontWeight: 600, color: color || 'var(--text-primary)' }}>
        {value}
      </div>
    </div>
  );
}

function getNodeRadius(node: NetworkNode): number {
  switch (node.type) {
    case 'creator':
      return 20;
    case 'token':
      return 14;
    case 'funder':
      return 18;
    case 'cluster':
      return 24;
    default:
      return 16;
  }
}

function getNodeIcon(node: NetworkNode): string {
  // Return single-character text labels for node icons in SVG
  switch (node.type) {
    case 'creator':
      return 'C';
    case 'token':
      const status = (node.data as TokenNodeData).status;
      return status === 'rugged' ? 'X' : status === 'graduated' ? 'G' : 'T';
    case 'funder':
      return 'F';
    case 'cluster':
      return '*';
    default:
      return '-';
  }
}

function formatNumber(num: number): string {
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toFixed(0);
}

function formatDate(timestamp: number): string {
  const days = Math.floor((Date.now() - timestamp) / (24 * 60 * 60 * 1000));
  if (days < 1) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
}
